<script setup>
import { watch, ref } from 'vue';
import { storeToRefs } from "pinia";
import { CircleCheck, CopyDocument, Link } from '@element-plus/icons-vue';
import { useFVStore } from '../../stores/formvibes';
import { axiosPost } from '../../Axios/post';

const store = useFVStore();
const i18n = window.fvGlobalVar?.i18n;
const isAuthenticated = ref(window.fvGlobalVar?.is_authenticated || false);
const clientId = ref(window.fvGlobalVar?.client_id || '');
const clientSecret = ref(window.fvGlobalVar?.client_secret || '');
const isLoading = ref(false);

const authDomainUrl = window?.fvGlobalVar?.tld || window?.fvGlobalVar?.domain?.host;

let restUrl = window?.fvGlobalVar.rest_url;
restUrl = restUrl+ 'form-vibes/googlesheets';


const onSave = async () => {
    isLoading.value = true;
    try {
        const { data } = await axiosPost({
            queryParams: {
                client_id: clientId.value,
                client_secret: clientSecret.value,
            },
            action: 'fv_google_sheet_authenticate',
        });

        if (data.success) {
            const url = data.data;
            window.location.href = url;
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('error', error.message);
    } finally {
        isLoading.value = false
    }
}

const onClickRest = () => {

    navigator.clipboard.writeText(restUrl);
    ElNotification({
        offset: 30,
        type:'success',
        dangerouslyUseHTMLString: true,
        message: '<strong>URI copied to clipboard</strong>',

    })
}
const onClickUrl = () => {
    navigator.clipboard.writeText(authDomainUrl);
    ElNotification({
        offset: 30,
        dangerouslyUseHTMLString: true,
        message: '<strong>Host name copied to clipboard</strong>',
        type:'success',
    })
}
</script>
<template>
    <div>
        <div class="fv-gsheet-wrapper">
            <div class="fv-gsheet-controls">
                <div class="fv-gsheet-control-wrapper gsheet-status">
                    <span class="">{{i18n?.status}}</span>
                    <el-tag
                        class="box-item"
                        :type="isAuthenticated ? 'success' : 'danger'"
                        effect="light"
                        >
                        {{ isAuthenticated ? 'Connected' : 'Not Connected' }}
                    </el-tag>
                    
                </div>
                <div class="fv-gsheet-control-wrapper">
                    <span >{{i18n?.client_id}}</span>
                    <div>
                        <el-input
                            v-model="clientId"
                            placeholder="Enter a client ID"
                        />
                    </div>
                </div>
                <div class="fv-gsheet-control-wrapper">
                    <span>
                        {{i18n?.client_secret}}
                    </span>
                    <div>
                        <el-input
                            v-model="clientSecret"
                            placeholder="Enter a client secret"
                        />
                    </div>
                </div>
                <div>
                    <el-button type="primary" @click="onSave" class="fv-save-button" >
                        {{isLoading
								? i18n?.authenticating
								: isAuthenticated
								? i18n?.re_authenticate
								: i18n?.authenticate}}
                    </el-button>
                </div>
            </div>
            <div class="fv-gsheet-description">
					<h3>{{i18n?.instructions}}</h3>
					<ol style="margin-left: 1em">
						<li>
							Go to 
							<a
								target="_blank"
								rel="noopener noreferrer"
								href="https://console.developers.google.com/"
							>
								Google Developer Console
							</a> 
							and create a <b>New Project</b>.
						</li>
						<li>
							Now select the newly created project from the projects dropdown.
						</li>
                        <li>From the left sidebar, go to API & Services -> Library.</li>
						<li>
                            Search for  <b>Google Sheets API</b> and <b>Google Drive API</b>, open each one, and click Enable to turn them on.
						</li>
						<li>
							On the left menu, select <b>OAuth Consent Screen</b> and then click <b>Get Started</b>.
						</li>
						<li>
                            Add your <b>App name</b> and Support email, then click Next. Choose <b>External</b> as the audience.
						</li>
						<li>
                            Add your <b>Contact info</b>, agree to the terms, and click <b>Create</b>.
						</li>
                        <li>On the left menu, click <b>Branding</b>, scroll down to <b>Authorized Domains</b>, and click <b>Add Domain.</b></li>
						<li>
							Copy the URL
							<b>
                                    <el-input readonly style="background-color: transparent; width: 240px;"
                                    :value="authDomainUrl" size="small" class="fv-googlesheet-input">
                                    <template #suffix>
                                        <el-icon @click.stop="onClickUrl" size="18">
                                            <CopyDocument />
                                        </el-icon>
                                    </template>
                                    </el-input>
							</b>
							and paste it in the <b>Authorized domain.</b>
						</li>
						<li>
                            Go to the Audience menu in the sidebar and click <b>Publish App.</b>
                            <!-- Please set the publishing status as in 
							<b>production</b>, otherwise you might get a 403 error. -->
						</li>

						<li>
							Now, go to the client option in the side menu and click on <b>Create Client</b> button.
						</li>
                        <li>Choose your <b>App type</b> and enter a name.</li>
						
						<li>
							Now go to <b>Authorised redirect URIs</b> and click <b>Add URI</b>
							.
						</li>
						<li>
							Paste this URL 
							<b>
								<i>
									<el-input
                                        readonly
                                        style="background-color: transparent; width: 450px;"
                                        :value="`${restUrl}`"
                                        size="small"
                                        class="fv-gsheet-input"
                                     >
                                     <template #suffix>
                                        <el-icon @click.stop="onClickRest" size="18">
                                            <CopyDocument />
                                        </el-icon>
                                    </template>
                                     </el-input>
								</i>
							</b> 
							in <b>Authorized redirect URIs</b>, click <b>Create</b>.
							<br />
							<span class="fv-gsheet-note">
								Note: It may take five minutes to a few hours for settings to
								take effect.
							</span>
						</li>
						<li>
							Copy <b>Client ID</b> and <b>Client Secret</b> from newly created
							app and put them in their respective fields(on the left).
						</li>
						<li>
							Click <b>Authenticate</b>, if appears <b>App is not verified</b>
							error click <b>show advanced</b> and then <b>Go to App</b>.
							<br />
							<span class="fv-ghseet-note">
								Note: Please check all the required scopes(permissions) when you
								Authenticate.
							</span>
						</li>
					</ol>
                    <div v-if="isAuthenticated">
                        <h3>Re Authenticate?</h3>
                        <span>
                            Try re-authorizing if you face issues. Go to 
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href="https://myaccount.google.com/permissions"
                            >
                                <b>Google App Permissions</b>
                            </a> 
                            and hit <b>REMOVE ACCESS</b> on any previous authorization of
                            this app. Now click on the <b>Re Authenticate</b> button and
                            finish the authorization process again.
                        </span>
                    </div>
				</div>
        </div>
    </div>
</template>